import React, {useEffect, useMemo, useState} from 'react';
import {Box, Checkbox, Collapse, FormControlLabel, IconButton, List, ListItem, Typography,} from '@mui/material';
import {ExpandLess, ExpandMore} from '@mui/icons-material';
import ApiClient from "../sdk/ApiClient";
import {useKeycloak} from "@react-keycloak/web";

const GroupSelection = ({onSelectedGroupsAndMembersChanged}) => {

    const {keycloak} = useKeycloak();

    // États pour gérer la sélection et l'affichage
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedMembers, setSelectedMembers] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});
    const [groups, setGroups] = useState([]);

    const apiClientInstance = useMemo(() => new ApiClient(keycloak.token), [keycloak.token]);

    // useEffect pour exécuter fetchMissions au chargement de la page
    useEffect(() => {
        // Méthode pour récupérer les missions
        const fetchGroups = async () => {
            try {
                const data = await apiClientInstance.getGroups()
                setGroups(data); // Stocke les missions dans l'état
            } catch (error) {
                console.error("Erreur lors de la récupération des groupes :", error);
            }
        }

        fetchGroups();
    }, [apiClientInstance]);

    useEffect(() => {
        onSelectedGroupsAndMembersChanged(selectedGroups, selectedMembers);
    }, [selectedGroups, selectedMembers, onSelectedGroupsAndMembersChanged
    ]);

    // Gestion de la sélection des groupes
    const handleGroupSelect = (groupId, isSelected) => {
        if (isSelected) {
            // Ajouter tous les membres à la sélection si le groupe entier est coché
            setSelectedGroups((prev) => [...prev, groupId]);

            // Supprimer les entrées correspondantes dans selectedMembers
            setSelectedMembers((prev) => {
                const updatedMembers = {...prev}; // Copier l'état actuel
                delete updatedMembers[groupId]; // Supprimer l'entrée associée au groupId
                return updatedMembers; // Retourner l'état mis à jour
            });


        } else {
            // Désélectionner tout le groupe et ses membres
            setSelectedGroups((prev) => prev.filter((id) => id !== groupId));

            // Supprimer les entrées correspondantes dans selectedMembers
            setSelectedMembers((prev) => {
                const updatedMembers = {...prev}; // Copier l'état actuel
                delete updatedMembers[groupId]; // Supprimer l'entrée associée au groupId
                return updatedMembers; // Retourner l'état mis à jour
            });

        }
    };

    // Gestion de l'affichage (déployer/replier les membres du groupe)
    const toggleGroupExpand = (groupId) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };

    // Gestion de la sélection/désélection d'un membre individuel
    const handleMemberSelect = (groupId, member, isSelected) => {
        setSelectedMembers((prev) => {
            const currentMembers = prev[groupId] || [];
            const updatedMembers = isSelected
                ? [...currentMembers, member]
                : currentMembers.filter((m) => m !== member);
            return {...prev, [groupId]: updatedMembers};
        });

        // Si tous les membres du groupe sont sélectionnés, coche automatiquement le groupe
        const group = groups.find((g) => g.id === groupId);
        const hasSelectedAll = isSelected
            ? (selectedMembers[groupId]?.length || 0) + 1 === group?.candidates.length
            : selectedMembers[groupId]?.length - 1 === group?.candidates.length;

        if (hasSelectedAll) {
            setSelectedGroups((prev) => [...prev, groupId]);
        } else {
            setSelectedGroups((prev) => prev.filter((id) => id !== groupId));
        }

    };

    // génère la liste des groupes sélectionnés
    const generateSelectedGroupListItem = (groupId) => {
        var group = groups.find((g) => g.id === groupId);
        if (group != null) {
            return <ListItem key={group.id} dense>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={true}
                            onChange={(e) =>
                                handleGroupSelect(group.id, e.target.checked)
                            }
                        />
                    }
                    label={<Typography sx={{fontWeight: "bold"}}>
                        {group.name.toUpperCase()}
                    </Typography>
                    }
                />
            </ListItem>
        }
    }

    // génère la liste des membres sélectionnés et leur groupe
    const generateSelectedMembersListItem = (groupId, members) => {
        // debugger
        var group = groups.find((g) => g.id === Number(groupId));
        if (group != null && members.length > 0) {
            return <List>
                <Box key={group.id} marginBottom={2}>
                    <Box display="flex" alignItems="top">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={true}
                                    onChange={(e) =>
                                        handleGroupSelect(group.id, false)
                                    }
                                />
                            }
                            style={{alignSelf: "flex-start"}}

                            label={<Typography sx={{fontWeight: "bold"}}>
                                {group.name.toUpperCase()}
                            </Typography>
                            }
                        />
                    </Box>
                    <List>
                        {members.map((member) => (
                            <ListItem key={member.id} dense>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                selectedMembers[group.id]?.includes(member) ||
                                                false
                                            }
                                            onChange={(e) =>
                                                handleMemberSelect(
                                                    group.id,
                                                    member,
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label={member.firstName.toUpperCase() + ' ' + member.lastName.toUpperCase()}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </List>
        }
    }

    return (
        <Box id={"group_selection_box"} display="flex" sx={{overflowY: "hidden"}}>
            {/* Colonne de gauche : Sélection */}
            <Box
                flex={1}
                paddingRight={2}
                borderRight="1px solid #ddd"
                sx={{overflowY: "auto"}}
                height={"75vh"}
            >
                <Typography variant="h5">Sélection des groupes et membres</Typography>
                <List>
                    {groups.map((group) => {
                        const isGroupSelected = selectedGroups.includes(group.id);
                        const isGroupExpanded = expandedGroups[group.id] || false;

                        // Vérifie si tous les membres sont sélectionnés
                        const areAllMembersSelected =
                            selectedMembers[group.id]?.length === group.candidates.length;

                        return (
                            <Box key={group.id} marginBottom={2}>
                                {/* En-tête du groupe (case à cocher + icône pour déployer) */}
                                <Box display="flex" alignItems="center">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isGroupSelected || areAllMembersSelected}
                                                onChange={(e) =>
                                                    handleGroupSelect(group.id, e.target.checked)
                                                }
                                            />
                                        }
                                        label={<Typography sx={{fontWeight: "bold"}}>
                                            {group.name.toUpperCase()}
                                        </Typography>
                                        }

                                    />

                                    <IconButton onClick={() => toggleGroupExpand(group.id)}>
                                        {isGroupExpanded ? <ExpandLess/> : <ExpandMore/>}
                                    </IconButton>
                                </Box>

                                {/* Membres du groupe (affichés si le groupe est déployé) */}
                                <Collapse in={isGroupExpanded} timeout="auto" unmountOnExit>
                                    <List>
                                        {group.candidates.map((member) => (
                                            <ListItem key={member.id} dense>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={
                                                                selectedMembers[group.id]?.includes(member) ||
                                                                false
                                                            }
                                                            onChange={(e) =>
                                                                handleMemberSelect(
                                                                    group.id,
                                                                    member,
                                                                    e.target.checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label={member.firstName.toUpperCase() + ' ' + member.lastName.toUpperCase()}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </Box>
                        );
                    })}
                </List>
            </Box>

            {/* Colonne de droite : Résumé */}
            <Box flex={1}
                 paddingLeft={2}
                 borderRight="1px solid #ddd"
                 sx={{overflowY: "auto"}}
                 height={"75vh"}
            >
                {/* Résumé des sélections */}
                <Typography variant="h5">
                    Résumé de la sélection :
                </Typography>
                {selectedGroups.length > 0 &&
                    <Typography variant="h6" marginTop={2}>
                        Groupes sélectionnés :
                        <List> {selectedGroups.map((groupId) => (generateSelectedGroupListItem(groupId)))}</List>

                    </Typography>
                }
                {Object.entries(selectedMembers).length > 0 &&
                    <Typography variant="h6" marginTop={2}>
                        Membres sélectionnés :
                        {
                            Object.entries(selectedMembers).map(([groupId, members]) => (
                            generateSelectedMembersListItem(groupId, members)

                        ))}
                    </Typography>
                }
                {selectedGroups.length === 0 && Object.entries(selectedMembers).length === 0 &&
                    <Typography variant="h6" marginTop={2}>
                        Pas de sélection
                    </Typography>
                }
            </Box>
        </Box>
    );
};

export default GroupSelection;
